import React, { useCallback, useContext, useEffect, useState } from "react";
import { Flag, Plus, Target, Trash2 } from "lucide-react";
import { AuthContext } from "../AuthContext";
import { API_BASE_URL } from "../api";
import { Button, Field, MetricCard, SectionHeader, SelectField, SurfaceCard, TextareaField } from "../components/designSystem";

export default function GoalsChallenges() {
  const { token } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", type: "goal" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/goals-challenges`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to load goals and challenges.");
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Failed to load goals and challenges.");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchItems();
    }
  }, [fetchItems, token]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/goals-challenges`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to save item.");
      }
      setForm({ title: "", description: "", type: "goal" });
      setItems((current) => [data, ...current]);
    } catch (err) {
      setError(err.message || "Failed to save item.");
    }
    setSaving(false);
  };

  const handleStatusChange = async (item, status) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/goals-challenges/${item._id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...item, status }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to update item.");
      }
      setItems((current) => current.map((entry) => (entry._id === item._id ? data : entry)));
    } catch (err) {
      setError(err.message || "Failed to update item.");
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/goals-challenges/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || "Failed to delete item.");
      }
      setItems((current) => current.filter((item) => item._id !== id));
    } catch (err) {
      setError(err.message || "Failed to delete item.");
    }
  };

  const activeCount = items.filter((item) => item.status === "active").length;
  const completedCount = items.filter((item) => item.status === "completed").length;

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <SurfaceCard className="overflow-hidden bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.18),_transparent_24%),linear-gradient(135deg,_#ffffff,_#f8fafc)] dark:bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.14),_transparent_24%),linear-gradient(135deg,_#111827,_#0f172a)]">
        <SectionHeader
          eyebrow="Goals and Challenges"
          title="Set targets with visible accountability"
          description="Trading improves faster when goals and discipline challenges are visible, specific, and regularly reviewed rather than vaguely remembered."
        />
        <div className="mt-6 grid gap-4 md:grid-cols-3">
          <MetricCard label="Total items" value={String(items.length)} helper="All saved goals and challenges." />
          <MetricCard label="Active" value={String(activeCount)} helper="Current commitments in motion." />
          <MetricCard label="Completed" value={String(completedCount)} helper="Items closed out successfully." />
        </div>
      </SurfaceCard>

      {error ? (
        <SurfaceCard>
          <p className="text-sm font-medium text-rose-600 dark:text-rose-300">{error}</p>
        </SurfaceCard>
      ) : null}

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <SurfaceCard>
          <SectionHeader eyebrow="New commitment" title="Create a goal or challenge" description="Use goals for directional improvement and challenges for tight behavioral rules you want to enforce." />
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <Field label="Title" name="title" value={form.title} onChange={handleChange} placeholder="Reduce revenge trades, complete weekly review" required />
            <TextareaField label="Description" name="description" value={form.description} onChange={handleChange} rows={5} placeholder="Describe what success looks like and why it matters." />
            <SelectField label="Type" name="type" value={form.type} onChange={handleChange}>
              <option value="goal">Goal</option>
              <option value="challenge">Challenge</option>
            </SelectField>
            <Button type="submit" variant="brand" disabled={saving}>
              <Plus className="h-4 w-4" />
              {saving ? "Saving..." : "Add Item"}
            </Button>
          </form>
        </SurfaceCard>

        <SurfaceCard>
          <SectionHeader eyebrow="Active board" title="Track what you are holding yourself to" description="Visible commitments reduce drift and make review sessions easier to structure." />
          <div className="mt-6 space-y-4">
            {loading ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading goals and challenges...</p>
            ) : items.length ? (
              items.map((item) => (
                <div key={item._id} className="rounded-3xl border border-slate-200 bg-slate-50 p-5 dark:border-gray-700 dark:bg-gray-900">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-950 dark:text-white">{item.title}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-300">{item.type}</span>
                        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${item.status === "completed" ? "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300" : item.status === "archived" ? "border-slate-200 bg-slate-50 text-slate-700 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-200" : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"}`}>{item.status}</span>
                      </div>
                    </div>
                    <Button type="button" variant="danger" className="px-3 py-2" onClick={() => handleDelete(item._id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {item.description ? (
                    <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">{item.description}</p>
                  ) : null}
                  <div className="mt-5 flex flex-wrap gap-3">
                    <Button type="button" variant="secondary" onClick={() => handleStatusChange(item, "active")}>
                      <Target className="h-4 w-4" />
                      Active
                    </Button>
                    <Button type="button" variant="brand" onClick={() => handleStatusChange(item, "completed")}>
                      <Flag className="h-4 w-4" />
                      Complete
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => handleStatusChange(item, "archived")}>
                      Archive
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-3xl border border-dashed border-slate-300 p-6 text-sm text-slate-500 dark:border-gray-700 dark:text-slate-400">
                No goals or challenges yet. Add one concrete commitment to start making reviews more accountable.
              </div>
            )}
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
}
